import { useState } from "react";
import { Calculator, ArrowRight, DollarSign, Download } from "lucide-react";
import QstLayout from "./QstLayout";
import QstAssistantWidget from "./QstAssistantWidget";
import { qstCalcRework, qstCalcInjury } from "./api";

const REWORK_FIELDS = [
  ["direct_labor",              "Direct labor cost"],
  ["overtime",                  "Overtime"],
  ["materials",                 "Materials"],
  ["equipment",                 "Equipment"],
  ["demolition_disposal",       "Demolition & disposal"],
  ["reinstallation",            "Reinstallation labor"],
  ["supervision",               "Supervision"],
  ["engineering",               "Design / engineering rework"],
  ["inspection",                "Inspection / QA time"],
  ["schedule_delay_days",       "Schedule delay (days)"],
  ["schedule_delay_cost_per_day", "Delay cost per day"],
  ["penalties",                 "Liquidated damages / penalties"],
  ["insurance_legal",           "Insurance / legal"],
  ["lost_productivity",         "Lost productivity"],
  ["other",                     "Other"],
];

const INJURY_FIELDS = [
  ["medical_cost",       "Medical cost"],
  ["lost_time_wages",    "Lost-time wages"],
  ["replacement_labor",  "Replacement labor"],
  ["overtime",           "Overtime"],
  ["investigation",      "Investigation"],
  ["training",           "Retraining"],
  ["equipment_damage",   "Equipment damage"],
  ["schedule_impact",    "Schedule impact"],
  ["insurance",          "Insurance"],
  ["legal",              "Legal"],
  ["administrative",     "Administrative"],
  ["other_indirect",     "Other indirect"],
];

function CalcCard({ mode }) {
  const isRework = mode === "rework";
  const fields = isRework ? REWORK_FIELDS : INJURY_FIELDS;
  const [vals, setVals] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  async function calc() {
    setBusy(true);
    try {
      const payload = fields.reduce((o, [k]) => ({ ...o, [k]: parseFloat(vals[k] || 0) || 0 }), {});
      const r = isRework ? await qstCalcRework(payload) : await qstCalcInjury(payload);
      setResult(r);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-4">Inputs</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map(([k, label]) => (
            <label key={k} className="text-sm">
              <div className="text-slate-700 font-semibold">{label}</div>
              <div className="mt-1 relative">
                <DollarSign className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input type="number" min="0" step="0.01"
                  value={vals[k] || ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })}
                  className="w-full rounded-md border border-slate-300 pl-8 pr-3 py-2 text-sm focus:border-[#0B2545] focus:ring-2 focus:ring-[#0B2545]/10 outline-none"
                  data-testid={`qst-calc-${mode}-${k}`} />
              </div>
            </label>
          ))}
        </div>
        <button onClick={calc} disabled={busy}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-6 py-2.5 font-bold shadow-sm"
          data-testid={`qst-calc-${mode}-compute`}>
          {busy ? "Calculating…" : <>Calculate <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>

      <div className="rounded-xl border-2 border-[#0B2545] bg-[#0B2545]/[.03] p-6 h-fit sticky top-20">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Estimated Cost</div>
        {result ? (
          <>
            <div className="mt-3 text-5xl font-black text-[#0B2545]" data-testid={`qst-calc-${mode}-total`}>
              ${result.total_cost.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md bg-white border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Direct</div>
                <div className="text-lg font-black text-slate-900">${result.direct_cost.toLocaleString()}</div>
              </div>
              <div className="rounded-md bg-white border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Indirect</div>
                <div className="text-lg font-black text-slate-900">${(result.indirect_cost ?? result.indirect_cost_entered).toLocaleString()}</div>
              </div>
            </div>
            {result.breakdown && (
              <div className="mt-4">
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-2">Breakdown</div>
                <ul className="space-y-1.5 text-sm">
                  {Object.entries(result.breakdown).map(([k, v]) => (
                    <li key={k} className="flex justify-between text-slate-800">
                      <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                      <span className="font-semibold">${v.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.heuristic_indirect_low !== undefined && (
              <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <strong>Industry sanity check:</strong> Indirect costs are typically 2–4x direct → about
                {" "}${result.heuristic_indirect_low.toLocaleString()}–${result.heuristic_indirect_high.toLocaleString()}.
              </div>
            )}
            <p className="mt-4 text-[11px] italic text-slate-500">{result.disclaimer}</p>
            <button className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 hover:bg-white text-slate-800 px-4 py-2 text-sm font-bold">
              <Download className="w-4 h-4" /> Export PDF
            </button>
          </>
        ) : (
          <div className="mt-3 text-sm text-slate-500">Enter cost figures on the left and press Calculate.</div>
        )}
      </div>
    </div>
  );
}

export function QstReworkCalcPage() {
  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "calculator", toolLabel: "Rework Cost Calculator" }} />
      <section className="bg-gradient-to-b from-[#0B2545] to-[#1D4ED8] text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Tool</div>
          <h1 className="mt-2 text-4xl font-black tracking-tight">Rework Cost Calculator</h1>
          <p className="mt-3 text-slate-200 max-w-2xl">Show your team the real cost of rework — every hidden line item.</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 py-10"><CalcCard mode="rework" /></section>
    </QstLayout>
  );
}

export function QstInjuryCalcPage() {
  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "calculator", toolLabel: "Injury Cost Calculator" }} />
      <section className="bg-gradient-to-b from-[#0B2545] to-[#DC2626] text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-orange-200 font-bold">Tool</div>
          <h1 className="mt-2 text-4xl font-black tracking-tight">Injury Cost Calculator</h1>
          <p className="mt-3 text-white/90 max-w-2xl">Direct plus indirect — the numbers behind every incident.</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 py-10"><CalcCard mode="injury" /></section>
    </QstLayout>
  );
}
